import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Users, Users2, Video, Flag, CheckCircle2, XCircle, Clock, TrendingUp, AlertTriangle,
} from "lucide-react";

const CARDS = [
  { k: "profiles",              label: "Total profiles",       icon: Users,        color: "amber"   },
  { k: "profiles_pending",      label: "Pending profiles",     icon: Clock,        color: "amber"   },
  { k: "profiles_verified",     label: "Verified profiles",    icon: CheckCircle2, color: "emerald" },
  { k: "profiles_rejected",     label: "Rejected profiles",    icon: XCircle,      color: "rose"    },
  { k: "verifications_pending", label: "Pending verifications", icon: Video,        color: "amber"   },
  { k: "reports_total",         label: "Reports",              icon: Flag,         color: "rose"    },
  { k: "users",                 label: "Users",                icon: Users2,       color: "stone"   },
];

const COLOR = {
  amber:   { bg: "bg-amber-500/10",   border: "border-amber-500/30",   text: "text-amber-300"   },
  emerald: { bg: "bg-emerald-500/10", border: "border-emerald-500/30", text: "text-emerald-300" },
  rose:    { bg: "bg-rose-500/10",    border: "border-rose-500/30",    text: "text-rose-300"    },
  stone:   { bg: "bg-stone-800/60",   border: "border-stone-700",      text: "text-stone-200"   },
};

export default function AdminHome() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true); setError(null);
      try {
        const { data } = await api.get("/admin/stats");
        setStats(data);
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        setError({ status, detail: typeof detail === "string" ? detail : String(detail || e.message || "Failed to load stats") });
        // eslint-disable-next-line no-console
        console.warn("[AdminHome] stats:", status, detail);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.22em] text-amber-400">Overview</p>
      <h1 className="text-2xl sm:text-3xl font-serif text-stone-50 mt-1">Dashboard</h1>
      <p className="text-sm text-stone-400 mt-1">At-a-glance moderation stats.</p>

      {error && (
        <div className="mt-4 p-3 rounded-lg border border-rose-500/40 bg-rose-500/5 text-xs text-rose-200 flex items-start gap-2" data-testid="adm-stats-error">
          <AlertTriangle className="w-4 h-4 flex-none mt-0.5" />
          <div>
            <p className="font-medium">Could not load dashboard stats (HTTP {error.status ?? "?"})</p>
            <p className="text-rose-300/80 mt-0.5">{error.detail}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-6" data-testid="adm-stats-grid">
        {CARDS.map((c) => {
          const val = stats?.[c.k];
          const col = COLOR[c.color];
          const Icon = c.icon;
          const display = loading ? "…"
            : error ? "—"
            : (typeof val === "number" ? val : (val ?? 0));
          return (
            <div
              key={c.k}
              className={`rounded-xl p-4 border ${col.border} ${col.bg}`}
              data-testid={`adm-stat-${c.k}`}
            >
              <div className="flex items-center justify-between">
                <Icon className={`w-4 h-4 ${col.text}`} />
                <TrendingUp className="w-3 h-3 text-stone-600" />
              </div>
              <p className="text-2xl sm:text-3xl font-serif text-stone-50 mt-3">
                {display}
              </p>
              <p className="text-xs text-stone-400 mt-1">{c.label}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
